import React, { useState } from "react";
import { CalendarDays, Apple, ChevronDown } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

/**
 * Add-to-calendar dropdown — three providers from one button.
 *
 *  - Apple/iOS Calendar  → downloads an RFC-5545 .ics file. iOS Mail/Safari
 *    auto-prompts to add when the user taps the file.
 *  - Google Calendar     → opens calendar.google.com/calendar/render?action=TEMPLATE
 *  - Outlook (web)       → opens outlook.live.com/calendar/0/deeplink/compose
 *
 * Props: { kind: 'match'|'tournament'|'league'|'lesson', id: string,
 *          start: ISO, end: ISO|null, title: string, location?: string,
 *          description?: string, size?: 'sm'|'md', testIdSuffix?: string }
 */
function _toGCalDate(iso) {
  // Google expects UTC compact: 20260601T080000Z
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().replace(/[-:]|\.\d{3}/g, "");
}

function _googleUrl({ title, start, end, description, location }) {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title || "Event",
    dates: `${_toGCalDate(start)}/${_toGCalDate(end || start)}`,
  });
  if (description) params.set("details", description);
  if (location) params.set("location", location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function _outlookUrl({ title, start, end, description, location }) {
  const params = new URLSearchParams({
    rru: "addevent",
    path: "/calendar/action/compose",
    subject: title || "Event",
    startdt: start || "",
    enddt: end || start || "",
  });
  if (description) params.set("body", description);
  if (location) params.set("location", location);
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

export default function AddToCalendarButton({
  kind, id, start, end, title, location, description,
  size = "md", testIdSuffix,
}) {
  const [busy, setBusy] = useState(false);

  const downloadIcs = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/events/${kind}/${id}/ics`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      a.download = m ? m[1] : `${kind}-${id}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Calendar file downloaded — open it to add the event");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't generate calendar file");
    } finally { setBusy(false); }
  };

  const args = { title, start, end, description, location };
  const cls = size === "sm"
    ? "px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-1"
    : "px-3 py-2 rounded-lg text-sm flex items-center gap-1.5";
  const tid = testIdSuffix ? `add-to-calendar-${testIdSuffix}` : "add-to-calendar";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={tid}
          disabled={busy || !start}
          className={`pp-btn-secondary ${cls} disabled:opacity-50`}
          title={!start ? "This event has no start time" : "Add to calendar"}
        >
          <CalendarDays size={size === "sm" ? 12 : 14} />
          <span>{busy ? "Adding…" : "Add to calendar"}</span>
          <ChevronDown size={size === "sm" ? 11 : 13} className="opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10 text-white">
        <DropdownMenuItem
          onClick={downloadIcs}
          data-testid={`${tid}-apple`}
          className="text-sm cursor-pointer focus:bg-white/5 gap-2"
        >
          <Apple size={14} className="text-white/70" /> Apple / iOS Calendar
          <span className="text-[10px] text-zinc-500 ms-auto">.ics</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => window.open(_googleUrl(args), "_blank", "noopener,noreferrer")}
          data-testid={`${tid}-google`}
          className="text-sm cursor-pointer focus:bg-white/5 gap-2"
        >
          <span className="inline-block h-3.5 w-3.5 rounded-full bg-gradient-to-br from-[#4285F4] via-[#EA4335] to-[#FBBC04]" />
          Google Calendar
          <span className="text-[10px] text-zinc-500 ms-auto">opens new tab</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => window.open(_outlookUrl(args), "_blank", "noopener,noreferrer")}
          data-testid={`${tid}-outlook`}
          className="text-sm cursor-pointer focus:bg-white/5 gap-2"
        >
          <span className="inline-block h-3.5 w-3.5 rounded-sm bg-[#0078D4]" />
          Outlook Web
          <span className="text-[10px] text-zinc-500 ms-auto">opens new tab</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
